import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Loader2, QrCode, Download } from "lucide-react";
import { useInstances } from "@/hooks/useInstances";
import { Skeleton } from "@/components/ui/skeleton";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import type { Tables } from "@/integrations/supabase/types";

function ProfileCard({ profile, selected, onClick }: { profile: Tables<"warmup_profiles">; selected: boolean; onClick: () => void }) {
  const icon = profile.ui_icon || "❄️";
  const isSystem = profile.is_system_preset;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border transition-all ${
        selected
          ? "bg-primary/10 border-primary/30 ring-1 ring-offset-0 ring-primary text-primary"
          : "bg-secondary/20 border-warmz-glass-border/30 hover:bg-secondary/40"
      }`}
    >
      <div className="flex items-start gap-3">
        <span className="text-lg leading-none mt-0.5">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className={`text-sm font-semibold ${selected ? "text-primary" : "text-foreground"}`}>{profile.name}</p>
            {isSystem && (
              <Badge variant="outline" className="text-[10px] shrink-0 border-warmz-glass-border/50">Sistema</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {profile.min_daily_msgs}–{profile.max_daily_msgs} msgs/dia · {profile.period_start?.slice(0, 5)} – {profile.period_end?.slice(0, 5)}
          </p>
          {profile.description && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{profile.description}</p>
          )}
        </div>
      </div>
    </button>
  );
}

function QrCodeDisplay({ qrCode, isPolling }: { qrCode: string | null; isPolling: boolean }) {
  if (!qrCode) {
    return (
      <div className="w-56 h-56 rounded-2xl bg-secondary/40 flex items-center justify-center">
        {isPolling ? (
          <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
        ) : (
          <QrCode className="h-16 w-16 text-muted-foreground/50" />
        )}
      </div>
    );
  }
  return (
    <div className="relative">
      <img
        src={qrCode.startsWith("data:") ? qrCode : `data:image/png;base64,${qrCode}`}
        alt="QR Code"
        className="w-56 h-56 rounded-2xl"
      />
      <div className="absolute inset-0 rounded-2xl border-2 border-primary/30 animate-pulse" />
    </div>
  );
}

export default function NumberAdd() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { warmupProfiles, userProfile, instances, createInstance, pollQrCode, syncContacts } = useInstances();

  const [profileId, setProfileId] = useState("");
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [instanceId, setInstanceId] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const maxPorts = userProfile?.max_ports || 2;
  const systemProfiles = warmupProfiles
    .filter((p) => p.is_system_preset)
    .sort((a, b) => (a.min_daily_msgs ?? 0) - (b.min_daily_msgs ?? 0));
  const customProfiles = warmupProfiles.filter((p) => !p.is_system_preset);

  useEffect(() => {
    if (warmupProfiles.length > 0 && !profileId) {
      const sorted = warmupProfiles
        .filter((p) => p.is_system_preset)
        .sort((a, b) => (a.min_daily_msgs ?? 0) - (b.min_daily_msgs ?? 0));
      setProfileId(sorted[0]?.id || warmupProfiles[0].id);
    }
  }, [warmupProfiles, profileId]);

  useEffect(() => {
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    setIsPolling(false);
  }, []);

  const startPolling = useCallback((id: string) => {
    setIsPolling(true);
    pollingRef.current = setInterval(async () => {
      try {
        const result = await pollQrCode(id);
        if (result.state === "open") {
          stopPolling();
          queryClient.invalidateQueries({ queryKey: ["instances"] });
          setShowImportDialog(true);
        } else if (result.qr_code) {
          setQrCode(result.qr_code);
        }
      } catch { /* ignore */ }
    }, 5000);
  }, [pollQrCode, stopPolling, queryClient, navigate]);

  const handleCreate = async () => {
    if (!profileId) return;
    stopPolling();
    const result = await createInstance.mutateAsync({ profile_id: profileId });
    if (result?.instance?.id) {
      setInstanceId(result.instance.id);
      setQrCode(result.qr_code);
      startPolling(result.instance.id);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => { stopPolling(); navigate("/dashboard/numbers"); }}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground">Conectar Número</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            {instances.length}/{maxPorts} portas usadas
          </p>
        </div>
      </div>

      {!instanceId ? (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          {/* Profile Selection */}
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="p-6 space-y-4">
              <Label className="text-sm font-semibold">Perfil de Aquecimento</Label>
              {systemProfiles.length > 0 && (
                <>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Presets do Sistema</p>
                  <div className="space-y-2">
                    {systemProfiles.map((p) => (
                      <ProfileCard key={p.id} profile={p} selected={profileId === p.id} onClick={() => setProfileId(p.id)} />
                    ))}
                  </div>
                </>
              )}
              {customProfiles.length > 0 && (
                <>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium pt-2">Perfis Customizados</p>
                  <div className="space-y-2">
                    {customProfiles.map((p) => (
                      <ProfileCard key={p.id} profile={p} selected={profileId === p.id} onClick={() => setProfileId(p.id)} />
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Button
            onClick={handleCreate}
            disabled={!profileId || createInstance.isPending || instances.length >= maxPorts}
            className="w-full glow-orange-sm h-12 text-base"
          >
            {createInstance.isPending ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Plus className="h-5 w-5 mr-2" />}
            Criar e Gerar QR Code
          </Button>
        </motion.div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="glass-card border-warmz-glass-border/50">
            <CardContent className="flex flex-col items-center gap-6 p-8">
              <QrCodeDisplay qrCode={qrCode} isPolling={isPolling} />
              <div className="text-center space-y-2">
                <p className="text-sm font-medium text-foreground">Escaneie o QR Code com seu WhatsApp</p>
                <p className="text-xs text-muted-foreground">WhatsApp → Aparelhos Conectados → Conectar</p>
              </div>
              {isPolling && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Aguardando conexão...
                </div>
              )}
            </CardContent>
          </Card>
      </motion.div>
      )}

      {/* Import Contacts Dialog */}
      <Dialog open={showImportDialog} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Importar Contatos</DialogTitle>
            <DialogDescription>
              Deseja importar os contatos salvos neste WhatsApp para a sua lista de contatos?
              Números já existentes serão ignorados.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setShowImportDialog(false);
                navigate("/dashboard/numbers");
              }}
              disabled={isSyncing}
            >
              Agora não
            </Button>
            <Button
              className="gap-1.5"
              disabled={isSyncing}
              onClick={async () => {
                if (!instanceId) return;
                setIsSyncing(true);
                try {
                  await syncContacts.mutateAsync({ instance_id: instanceId });
                } catch { /* toast already handled */ }
                setIsSyncing(false);
                setShowImportDialog(false);
                navigate("/dashboard/numbers");
              }}
            >
              {isSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Sim, importar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
